/**
 * ONE map for the whole page, and a DOM node that outlives React.
 *
 * The map has to live in two places: a side card on the board, and the whole
 * main area once the guide starts leading (design §1.4, §2.2). Re-creating it
 * on that move would re-download tiles, re-bill them, and blink. So the
 * container is created once with `document.createElement` and merely
 * REPARENTED — moving a WebGL canvas between parents does not lose its context,
 * and `map.invalidateSize()` is what tells the SDK the frame it landed in.
 *
 * Between mounts the node is parked in an off-screen div rather than hidden
 * inside the layout: `display:none` gives the container a zero size, MapGL's
 * resize tracking believes it, and the map comes back one pixel wide.
 *
 * **The instance is module-level, not component-level, and that is the whole
 * point.** React 18 StrictMode mounts every effect twice in development. A map
 * built inside `useEffect` would therefore be built twice, and the first copy
 * would go on rendering — and billing — from a detached node where nobody would
 * ever see it (design §10). `acquireMap` is a memoised promise, so the second
 * mount gets the first map.
 */

import type * as MapGL from '@2gis/mapgl/types';
import type { Map as MapGLMap } from '@2gis/mapgl/types';

import { ensureSdk, mapKey, markLiveMapBroken } from './capability';
import { cssToken, styleIdForTheme } from './theme';

export type MapHandle = { map: MapGLMap; element: HTMLDivElement; sdk: typeof MapGL };

export type MapInit = { center: [number, number]; zoom: number };

let handle: MapHandle | null = null;
let pending: Promise<MapHandle | null> | null = null;
let parking: HTMLDivElement | null = null;
/** Watches the SLOT the map is attached to; see `watchSlot`. */
let observer: ResizeObserver | null = null;
let frame1: number | null = null;
let frame2: number | null = null;

/**
 * Where the container waits between mounts: on the page, with a real size, and
 * nowhere anybody can see it.
 */
function parkingLot(): HTMLDivElement {
  if (parking) return parking;
  parking = document.createElement('div');
  parking.setAttribute('aria-hidden', 'true');
  parking.style.cssText =
    'position:fixed;left:-10000px;top:0;width:360px;height:260px;pointer-events:none;';
  document.body.appendChild(parking);
  return parking;
}

/**
 * Builds the map, or resolves `null` when this session has no live map at all
 * (no key, unsupported browser, SDK that never arrived).
 *
 * The first caller's `center`/`zoom` win; every later caller frames the map
 * with `fitBounds` anyway.
 */
export function acquireMap(init: MapInit): Promise<MapHandle | null> {
  if (pending) return pending;
  pending = (async () => {
    const sdk = await ensureSdk();
    if (!sdk) return null;

    const element = document.createElement('div');
    element.className = 'mapgl-canvas';
    element.style.cssText = 'width:100%;height:100%;';
    parkingLot().appendChild(element);

    const map = new sdk.Map(element, {
      key: mapKey(),
      center: init.center,
      zoom: init.zoom,
      minZoom: 12,
      maxZoom: 19,
      // +/− at the right edge, centred (INV-PH-140): the corners belong to the
      // overlays in the navigation mode and to «назад» / the sheet in the chat's
      // full screen, and the middle of an edge is free at 360 px too. «Я здесь»
      // joins it from `MapLive` (`map/locateControl.ts`) outside the navigation.
      zoomControl: 'centerRight',
      trafficControl: false,
      scaleControl: false,
      // A walking route through a park: rotating and pitching it only loses
      // north, and on a phone both fire by accident.
      disableRotationByUserInteraction: true,
      disablePitchByUserInteraction: true,
      // Read, not hard-coded: the frame before the style loads is the board's
      // own sunken grey, so a dark theme does not flash white (design §1.5).
      defaultBackgroundColor: cssToken('--paper-sunk', '#f4f5f8'),
      style: styleIdForTheme(),
      /**
       * The LIGHT preset, and the reason is a console rather than a taste.
       *
       * Left on its default the SDK fetches 3D models
       * (`tile*-sdk.maps.2gis.com/metafiles/*&#47;models.json`,
       * `disk.2gis.com/styles/assets/models`) and its Draco decoder tries to
       * compile a decoder from a string — which our CSP refuses, since we do
       * not grant `unsafe-eval` and will not: `eval` on a page that renders
       * museum copy is a hole opened for a decoration. Measured on dev: ~120
       * warnings a minute, and the models never draw either way.
       *
       * A walking map of a park at zoom 15–17 wants none of it — flat tiles, a
       * line and some pins — so the preset is also the right picture, and on a
       * phone in a park the cheaper one. WHICH flags `light` actually carries
       * is the STYLE's answer rather than ours (`graphicsPreset` puts the map
       * in preset mode and the style supplies `modelsOff`, `skyOff`, …), so
       * the resolved set is logged once below instead of being assumed here.
       */
      graphicsPreset: 'light',
    });

    map.on('error', (event) => {
      // `invalidtilekey` and `rasterTileLoadError` are survivable — a tile is
      // missing, the map is not. These two are not: the canvas is gone, or the
      // style never arrived, and both leave a grey rectangle behind.
      if (event.type === 'webglcontextlost' || event.type === 'styleloaderror') {
        destroyMap();
        markLiveMapBroken(event.type);
      }
    });

    // What the style actually granted, once, so a live run can confirm the
    // models are off rather than us reading a preset NAME and believing it.
    map.once('styleload', () => {
      try {
        // eslint-disable-next-line no-console
        console.info('[map] graphics preset', map.getGraphicsPreset());
      } catch {
        // The method only exists in preset mode; not having it is not a fault.
      }
    });

    handle = { map, element, sdk };
    return handle;
  })();
  return pending;
}

/**
 * Re-reads the container's size on the frame AFTER the next one.
 *
 * **Measured on dev: the map rendered 278 of its 396 pixels**, the right third
 * a grey band — a WebGL canvas still sized for the 360×260 parking lot inside a
 * 396 px column. `attachMap` called `invalidateSize()` synchronously, in the
 * same tick as the `appendChild`, when the slot has not been laid out in its new
 * parent yet: the SDK measured the box it was moved OUT of.
 *
 * Two frames rather than one: the first is when style and layout for the new
 * parent are applied, the second is the first at which reading the box is
 * guaranteed to see them. One frame is right on most paths and wrong on the one
 * that matters here — a card mounting inside a panel that is itself animating
 * open. `ResizeObserver` below covers everything after that; this covers the
 * frame before it has fired.
 */
function invalidateAfterLayout(): void {
  cancelPending();
  if (typeof requestAnimationFrame !== 'function') {
    handle?.map.invalidateSize();
    return;
  }
  frame1 = requestAnimationFrame(() => {
    frame1 = null;
    frame2 = requestAnimationFrame(() => {
      frame2 = null;
      handle?.map.invalidateSize();
    });
  });
}

function cancelPending(): void {
  if (typeof cancelAnimationFrame === 'function') {
    if (frame1 !== null) cancelAnimationFrame(frame1);
    if (frame2 !== null) cancelAnimationFrame(frame2);
  }
  frame1 = null;
  frame2 = null;
}

/**
 * Follows the slot's size for as long as the map lives in it.
 *
 * The column is not a constant: the board panel slides open and shut over
 * `--panel-ms`, the navigation mode hands the map the whole screen, and a
 * desktop window is resized by hand. `enableTrackResize` is the SDK's own
 * watcher and it is left on — this is the belt for the case it is measured
 * missing, a container whose size changes without the WINDOW changing.
 *
 * The callback invalidates SYNCHRONOUSLY and deliberately: a `ResizeObserver`
 * fires after layout and before paint, which is exactly the moment the box is
 * real. Deferring it a frame would make the map lag one frame behind every
 * frame of the panel's slide.
 */
function watchSlot(target: HTMLElement): void {
  stopWatchingSlot();
  if (typeof ResizeObserver !== 'function') return;
  observer = new ResizeObserver(() => handle?.map.invalidateSize());
  observer.observe(target);
}

function stopWatchingSlot(): void {
  observer?.disconnect();
  observer = null;
}

/**
 * Moves the container into `target` and tells the SDK its new size.
 *
 * **Two viewports are mounted at once during an ordinary card swap**, which is
 * why nothing here warns about it: `surfaceStore` keeps a removed node in
 * `exiting` for the length of its exit animation, so `map:nav` is still on
 * screen while `map:plan` is already mounting. Both are legitimate; the node
 * belongs to whichever asked for it last, and `parkMap` is what makes that
 * survive the loser's cleanup.
 *
 * Idempotent to the slot it is already in: re-appending would move the node to
 * the end of its own parent and cost a layout for nothing. The size is still
 * re-read, because a second call usually means something about the box changed.
 */
export function attachMap(target: HTMLElement): void {
  if (!handle) return;
  if (handle.element.parentElement === target) {
    handle.map.invalidateSize();
    return;
  }
  target.appendChild(handle.element);
  // Three passes at the same question, cheapest first: now (right whenever the
  // slot already had its layout), on the observer's initial callback, and two
  // frames on. `invalidateSize` is idempotent, so the extra ones cost a
  // measurement of a box that has not changed.
  handle.map.invalidateSize();
  watchSlot(target);
  invalidateAfterLayout();
}

/**
 * Returns the container to the parking lot — **only if it is still ours**.
 *
 * Live on dev: «Стоп» brought the board back, the side card drew its frame and
 * its totals, and the map area was EMPTY — no tiles, no pins, because the node
 * was sitting in the parking div. `map:nav` leaves `region:main` and `map:plan`
 * arrives in `region:side` on the same frame, but the removed card is held for
 * its exit animation, so the new viewport attached and the OLD one's cleanup
 * ran afterwards and took the node back.
 *
 * The check is the DOM's own answer rather than bookkeeping beside it: if this
 * slot is no longer the node's parent, somebody else has it and this cleanup
 * has nothing to clean. That also leaves the observer and the pending frames
 * alone — they belong to whoever holds the node now.
 */
export function parkMap(fromSlot: HTMLElement): void {
  if (!handle) return;
  if (handle.element.parentElement !== fromSlot) return;
  stopWatchingSlot();
  cancelPending();
  parkingLot().appendChild(handle.element);
}

export function currentMap(): MapGLMap | null {
  return handle?.map ?? null;
}

export function currentHandle(): MapHandle | null {
  return handle;
}

/**
 * TEST ONLY — the reparenting policy without a browser.
 *
 * `acquireMap` cannot run in this suite (`environment: 'node'`: `load()`
 * refuses outside a browser), and the sequence that produced the empty card —
 * attach, attach, park the first — is exactly what needs pinning. Same shape as
 * `capability.resetMapCapabilityForTests`.
 */
export function __setHandleForTests(next: MapHandle | null): void {
  handle = next;
  pending = next ? Promise.resolve(next) : null;
  parking = null;
  stopWatchingSlot();
  cancelPending();
}

/**
 * Tears the map down for good. Called when the SDK reports a lost context —
 * the sketch renderer takes over from there and never gives it back.
 */
export function destroyMap(): void {
  if (!handle) return;
  const dying = handle;
  handle = null;
  pending = Promise.resolve(null);
  stopWatchingSlot();
  cancelPending();
  try {
    dying.map.destroy();
  } catch {
    // A map that is already gone is exactly the state we wanted.
  }
  dying.element.remove();
}
